const DEFAULT_API_BASE_URL = 'http://localhost:3030';
const DEFAULT_APP_ORIGIN = 'http://localhost';
const HTML_CONTENT_TYPES = ['text/html', 'application/xhtml+xml'] as const;
const JSON_CONTENT_TYPE = 'application/json';
const ASSET_PATH_SEGMENT = '/assets/';
const ASSET_DESTINATIONS = [
  'audio',
  'font',
  'image',
  'manifest',
  'script',
  'style',
  'track',
  'video',
  'worker',
] as const;
const ASSET_FILE_EXTENSIONS = [
  '.avif',
  '.css',
  '.gif',
  '.ico',
  '.jpeg',
  '.jpg',
  '.js',
  '.json',
  '.map',
  '.mjs',
  '.mp3',
  '.mp4',
  '.png',
  '.svg',
  '.ts',
  '.txt',
  '.webm',
  '.webp',
  '.woff',
  '.woff2',
  '.xml',
] as const;
const TELEMETRY_HOSTS = [
  'analytics.google.com',
  'googletagmanager.com',
  'google-analytics.com',
  'sentry.io',
  'ingest.sentry.io',
] as const;
const TELEMETRY_HOST_KEYWORDS = ['sentry'] as const;
const KNOWN_THIRD_PARTY_HOSTS = [
  'api.mapbox.com',
  'events.mapbox.com',
  'tiles.mapbox.com',
  'api.stripe.com',
  'checkout.stripe.com',
  'js.stripe.com',
  'm.stripe.network',
] as const;
const ACCEPT_HEADER_NAME = 'accept';

export const REQUEST_CLASSIFICATION = {
  ASSET: 'asset',
  FIRST_PARTY_API: 'firstPartyApi',
  FIRST_PARTY_DOCUMENT: 'firstPartyDocument',
  TELEMETRY: 'telemetry',
  THIRD_PARTY: 'thirdParty',
  UNKNOWN: 'unknown',
} as const;

export type RequestClassification =
  (typeof REQUEST_CLASSIFICATION)[keyof typeof REQUEST_CLASSIFICATION];

export interface ClassifiedRequest {
  classification: RequestClassification;
  method: string;
  url: string;
  origin: string | undefined;
}

const getAppOrigin = (): string => {
  if (typeof window === 'undefined') {
    return DEFAULT_APP_ORIGIN;
  }

  return window.location.origin;
};

const getConfiguredApiOrigins = (): Set<string> => {
  const origins = new Set<string>();
  const configuredApiUrl =
    import.meta.env.VITE_PLANNER_API_URL ?? DEFAULT_API_BASE_URL;

  [configuredApiUrl, DEFAULT_API_BASE_URL].forEach((candidateUrl) => {
    try {
      origins.add(new URL(candidateUrl).origin);
    } catch {
      // Ignore invalid URLs so request classification stays resilient.
    }
  });

  return origins;
};

const hostMatches = (
  hostname: string,
  candidates: readonly string[]
): boolean => {
  return candidates.some(
    (candidateHostname) =>
      hostname === candidateHostname || hostname.endsWith(`.${candidateHostname}`)
  );
};

const resolveUrl = (input: RequestInfo | URL): URL | undefined => {
  try {
    if (typeof input === 'string') {
      return new URL(input, getAppOrigin());
    }

    if (input instanceof URL) {
      return input;
    }

    if (input instanceof Request) {
      return new URL(input.url, getAppOrigin());
    }

    return undefined;
  } catch {
    return undefined;
  }
};

const getMethod = (input: RequestInfo | URL, init?: RequestInit): string => {
  const requestMethod = input instanceof Request ? input.method : undefined;
  return (init?.method ?? requestMethod ?? 'GET').toUpperCase();
};

const getHeaders = (input: RequestInfo | URL, init?: RequestInit): Headers => {
  const mergedHeaders = new Headers(
    input instanceof Request ? input.headers : undefined
  );

  if (init?.headers) {
    const overrideHeaders = new Headers(init.headers);
    overrideHeaders.forEach((headerValue, headerName) => {
      mergedHeaders.set(headerName, headerValue);
    });
  }

  return mergedHeaders;
};

const isDocumentRequest = (
  input: RequestInfo | URL,
  init?: RequestInit
): boolean => {
  const request = input instanceof Request ? input : undefined;
  const headers = getHeaders(input, init);
  const acceptHeaderValue =
    headers.get(ACCEPT_HEADER_NAME)?.toLowerCase() ?? '';

  return (
    request?.destination === 'document' ||
    request?.mode === 'navigate' ||
    HTML_CONTENT_TYPES.some((contentType) =>
      acceptHeaderValue.includes(contentType)
    )
  );
};

const isJsonRequest = (input: RequestInfo | URL, init?: RequestInit): boolean => {
  const headers = getHeaders(input, init);
  const acceptHeaderValue =
    headers.get(ACCEPT_HEADER_NAME)?.toLowerCase() ?? '';
  const contentTypeHeaderValue =
    headers.get('content-type')?.toLowerCase() ?? '';

  return (
    acceptHeaderValue.includes(JSON_CONTENT_TYPE) ||
    contentTypeHeaderValue.includes(JSON_CONTENT_TYPE)
  );
};

const hasAssetExtension = (pathname: string): boolean => {
  const normalizedPathname = pathname.toLowerCase();

  return ASSET_FILE_EXTENSIONS.some((extension) =>
    normalizedPathname.endsWith(extension)
  );
};

const isAssetRequest = (
  input: RequestInfo | URL,
  resolvedUrl: URL
): boolean => {
  const request = input instanceof Request ? input : undefined;
  const pathname = resolvedUrl.pathname.toLowerCase();

  return (
    pathname.includes(ASSET_PATH_SEGMENT) ||
    hasAssetExtension(pathname) ||
    Boolean(
      request?.destination &&
        ASSET_DESTINATIONS.includes(
          request.destination as (typeof ASSET_DESTINATIONS)[number]
        )
    )
  );
};

export const classifyRequest = (
  input: RequestInfo | URL,
  init?: RequestInit
): ClassifiedRequest => {
  const method = getMethod(input, init);
  const resolvedUrl = resolveUrl(input);
  const rawUrl =
    typeof input === 'string'
      ? input
      : input instanceof URL
        ? input.toString()
        : input instanceof Request
          ? input.url
          : 'unknown';

  if (!resolvedUrl) {
    return {
      classification: REQUEST_CLASSIFICATION.UNKNOWN,
      method,
      url: rawUrl,
      origin: undefined,
    };
  }

  const hostname = resolvedUrl.hostname.toLowerCase();
  const requestUrl = resolvedUrl.toString();

  if (isAssetRequest(input, resolvedUrl)) {
    return {
      classification: REQUEST_CLASSIFICATION.ASSET,
      method,
      url: requestUrl,
      origin: resolvedUrl.origin,
    };
  }

  if (
    hostMatches(hostname, TELEMETRY_HOSTS) ||
    TELEMETRY_HOST_KEYWORDS.some((keyword) => hostname.includes(keyword))
  ) {
    return {
      classification: REQUEST_CLASSIFICATION.TELEMETRY,
      method,
      url: requestUrl,
      origin: resolvedUrl.origin,
    };
  }

  if (hostMatches(hostname, KNOWN_THIRD_PARTY_HOSTS)) {
    return {
      classification: REQUEST_CLASSIFICATION.THIRD_PARTY,
      method,
      url: requestUrl,
      origin: resolvedUrl.origin,
    };
  }

  if (getConfiguredApiOrigins().has(resolvedUrl.origin)) {
    return {
      classification: REQUEST_CLASSIFICATION.FIRST_PARTY_API,
      method,
      url: requestUrl,
      origin: resolvedUrl.origin,
    };
  }

  if (resolvedUrl.origin === getAppOrigin()) {
    return {
      classification:
        isDocumentRequest(input, init) || !isJsonRequest(input, init)
          ? REQUEST_CLASSIFICATION.FIRST_PARTY_DOCUMENT
          : REQUEST_CLASSIFICATION.FIRST_PARTY_API,
      method,
      url: requestUrl,
      origin: resolvedUrl.origin,
    };
  }

  return {
    classification: REQUEST_CLASSIFICATION.THIRD_PARTY,
    method,
    url: requestUrl,
    origin: resolvedUrl.origin,
  };
};

export const isRedirectEligibleRequest = (
  classification: RequestClassification
): boolean => {
  return classification === REQUEST_CLASSIFICATION.FIRST_PARTY_DOCUMENT;
};

export const shouldShowApiErrorToast = (
  classification: RequestClassification
): boolean => {
  return classification === REQUEST_CLASSIFICATION.FIRST_PARTY_API;
};

export const shouldReportRequestToSentry = (
  classification: RequestClassification
): boolean => {
  return (
    classification === REQUEST_CLASSIFICATION.FIRST_PARTY_API ||
    classification === REQUEST_CLASSIFICATION.FIRST_PARTY_DOCUMENT
  );
};
